import type { HeaderMenuConfiguration } from "../interfaces";

// Constants
import actions from "@/Constants/actionsList";
import positions from "@/Constants/positions";

// Helpers
import { getPositionIcon } from "@Common/helpers/Positions/icons";

// Icons
import PayrollClosing from "@/assets/icons/header/washouts/PayrollClosing.svg";
import PayrollClosingDark from "@/assets/icons/header/washouts/PayrollClosingDark.svg";
import WashoutReview from "@/assets/icons/header/washouts/WashoutReview.svg";
import WashoutReviewDark from "@/assets/icons/header/washouts/WashoutReviewDark.svg";
import SupportCenter from "@/assets/icons/header/washouts/SupportCenter.svg";
import SupportCenterDark from "@/assets/icons/header/washouts/SupportCenterDark.svg";

const MATCHES_CUSTOMER_WITH_IDEAL_CAR = "Matches customers with their ideal car";

const WashoutsMenuConfiguration: HeaderMenuConfiguration = {
	title: "Washouts",
	actions: [
		actions.VIEW_GENERAL_MANAGER_WASHOUTS,
		actions.VIEW_SALES_ASSOCIATE_WASHOUTS,
		actions.VIEW_SALES_MANAGER_WASHOUTS,
		actions.VIEW_GENERAL_SALES_MANAGER_WASHOUTS,
		actions.VIEW_FINANCE_MANAGER_WASHOUTS,
		actions.VIEW_FINANCE_DIRECTOR_WASHOUTS,
		actions.VIEW_BDC_MANAGER_WASHOUTS,
		actions.VIEW_BDC_ASSOCIATE_WASHOUTS,
		actions.VIEW_SERVICE_TECHNICIAN_WASHOUTS,
		actions.VIEW_SERVICE_ADVISOR_WASHOUTS,
		actions.VIEW_SERVICE_MANAGER_WASHOUTS,
		actions.VIEW_SERVICE_DIRECTOR_WASHOUTS,
		actions.VIEW_SERVICE_FOREMAN_WASHOUTS,
		actions.VIEW_INTERNAL_SERVICE_ADVISOR_WASHOUTS,
		actions.VIEW_BODY_SHOP_MANAGER_WASHOUTS,
		actions.VIEW_BODY_SHOP_PRODUCT_MANAGER_WASHOUTS,
		actions.VIEW_BODY_SHOP_FOREMAN_WASHOUTS,
		actions.VIEW_BODY_SHOP_ESTIMATOR_WASHOUTS,
		actions.VIEW_BODY_SHOP_PAINTER_WASHOUTS,
		actions.VIEW_BODY_SHOP_TECHNICIAN_WASHOUTS,
		actions.VIEW_PARTS_COUNTER_ASSOCIATE_WASHOUTS,
		actions.VIEW_PARTS_MANAGER_WASHOUTS,
		actions.VIEW_PARTS_DIRECTOR_WASHOUTS,
		actions.VIEW_WASHOUT_REVIEW,
		actions.VIEW_COMMISSION_CYCLE_CLOSING,
		actions.VIEW_SUPPORT_CENTER,
	],
	menuConfiguration: {
		submenuMinWidth: 482,
		submenuHeight: "calculated",
		sections: [
			{
				name: "Location",
				menuItemGroups: [
					{
						id: 0,
						menuItems: [
							{
								label: "General Manager",
								description: MATCHES_CUSTOMER_WITH_IDEAL_CAR,
								icon: getPositionIcon({ position: positions.LOCATION.GENERAL_MANAGER }),
								url: "",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_GENERAL_MANAGER_WASHOUTS,
									position: positions.LOCATION.GENERAL_MANAGER,
								},
							},
						],
					},
				],
			},
			{
				name: "Sales",
				menuItemGroups: [
					{
						id: 0,
						menuItems: [
							{
								label: "General Sales Manager",
								description: "Oversees sales department",
								icon: getPositionIcon({ position: positions.SALES.GENERAL_SALES_MANAGER }),
								url: "/washouts/general-sales-manager",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_GENERAL_SALES_MANAGER_WASHOUTS,
									position: positions.SALES.GENERAL_SALES_MANAGER,
								},
							},
						],
					},
					{
						id: 1,
						menuItems: [
							{
								label: "Sales Manager",
								description: "Sales associate supervisor/motivator",
								icon: getPositionIcon({ position: positions.SALES.SALES_MANAGER }),
								url: "/washouts/sales-manager",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_SALES_MANAGER_WASHOUTS,
									position: positions.SALES.SALES_MANAGER,
								},
							},
						],
					},
					{
						id: 2,
						menuItems: [
							{
								label: "Sales Associates",
								description: MATCHES_CUSTOMER_WITH_IDEAL_CAR,
								icon: getPositionIcon({ position: positions.SALES.SALES_ASSOCIATE }),
								url: "/washouts/sales-associate",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_SALES_ASSOCIATE_WASHOUTS,
									position: positions.SALES.SALES_ASSOCIATE,
								},
							},
						],
					},
				],
			},
			{
				name: "F&I",
				menuItemGroups: [
					{
						id: 0,
						menuItems: [
							{
								label: "Finance Director",
								description: "Leads the finance department",
								icon: getPositionIcon({ position: positions.FINANCE.FINANCE_DIRECTOR }),
								url: "/washouts/finance-director",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_FINANCE_DIRECTOR_WASHOUTS,
									position: positions.FINANCE.FINANCE_DIRECTOR,
								},
							},
						],
					},
					{
						id: 1,
						menuItems: [
							{
								label: "F&I Manager",
								description: "Structures vehicle deals for customers",
								icon: getPositionIcon({ position: positions.FINANCE.FINANCE_MANAGER }),
								url: "/washouts/finance-manager",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_FINANCE_MANAGER_WASHOUTS,
									position: positions.FINANCE.FINANCE_MANAGER,
								},
							},
						],
					},
				],
			},
			{
				name: "BDC",
				menuItemGroups: [
					{
						id: 0,
						menuItems: [
							{
								label: "BDC Manager",
								description: "Leads the BDC department",
								icon: getPositionIcon({ position: positions.BDC.BDC_MANAGER }),
								url: "",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_BDC_MANAGER_WASHOUTS,
									position: positions.BDC.BDC_MANAGER,
								},
							},
						],
					},
					{
						id: 1,
						menuItems: [
							{
								label: "BDC Service Associate",
								description: "Sets up all service interactions",
								icon: getPositionIcon({ position: positions.BDC.BDC_SERVICE_ASSOCIATE }),
								url: "",
								accessConfiguration: false,
							},
						],
					},
					{
						id: 2,
						menuItems: [
							{
								label: "BDC Sales Associate",
								description: "Handles sales leads and inquiries",
								icon: getPositionIcon({ position: positions.BDC.BDC_SALES_ASSOCIATE }),
								url: "",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_BDC_ASSOCIATE_WASHOUTS,
									position: positions.BDC.BDC_SALES_ASSOCIATE,
								},
							},
						],
					},
				],
			},
			{
				name: "Service",
				menuItemGroups: [
					{
						id: 0,
						menuItems: [
							{
								label: "Service Directors",
								description: "Directs all service related activities",
								icon: getPositionIcon({ position: positions.SERVICE.SERVICE_DIRECTOR }),
								url: "/washouts/service-director",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_SERVICE_DIRECTOR_WASHOUTS,
									position: positions.SERVICE.SERVICE_DIRECTOR,
								},
							},
						],
					},
					{
						id: 1,
						menuItems: [
							{
								label: "Service Manager",
								description: "Supervises all service processes",
								icon: getPositionIcon({ position: positions.SERVICE.SERVICE_MANAGER }),
								url: "/washouts/service-manager",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_SERVICE_MANAGER_WASHOUTS,
									position: positions.SERVICE.SERVICE_MANAGER,
								},
							},
						],
					},
					{
						id: 2,
						menuItems: [
							{
								label: "Service Foreman",
								description: "Foresees all service processes",
								icon: getPositionIcon({ position: positions.SERVICE.SERVICE_FOREMAN }),
								url: "/washouts/service-foreman",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_SERVICE_FOREMAN_WASHOUTS,
									position: positions.SERVICE.SERVICE_FOREMAN,
								},
							},
						],
					},
					{
						id: 3,
						menuItems: [
							{
								label: "Service Advisors",
								description: "Handles customers vehicle needs",
								icon: getPositionIcon({ position: positions.SERVICE.SERVICE_ADVISOR }),
								url: "/washouts/service-advisor",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_SERVICE_ADVISOR_WASHOUTS,
									position: positions.SERVICE.SERVICE_ADVISOR,
								},
							},
						],
					},
					{
						id: 4,
						menuItems: [
							{
								label: "Internal Service Advisors",
								description: "Handles customer vehicle needs",
								icon: getPositionIcon({ position: positions.SERVICE.INTERNAL_SERVICE_ADVISOR }),
								url: "/washouts/internal-service-advisor",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_INTERNAL_SERVICE_ADVISOR_WASHOUTS,
									position: positions.SERVICE.INTERNAL_SERVICE_ADVISOR,
								},
							},
						],
					},
					{
						id: 5,
						menuItems: [
							{
								label: "Service Technicians",
								description: "Repair and maintenance of vehicles",
								icon: getPositionIcon({ position: positions.SERVICE.SERVICE_TECHNICIAN }),
								url: "/washouts/service-technician",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_SERVICE_TECHNICIAN_WASHOUTS,
									position: positions.SERVICE.SERVICE_TECHNICIAN,
								},
							},
						],
					},
				],
			},
			{
				name: "Body Shop",
				menuItemGroups: [
					{
						id: 0,
						menuItems: [
							{
								label: "Body Shop Manager",
								description: "Supervises the body shop department",
								icon: getPositionIcon({ position: positions.BODY_SHOP.BODY_SHOP_MANAGER }),
								url: "/washouts/body-shop-manager",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_BODY_SHOP_MANAGER_WASHOUTS,
									position: positions.BODY_SHOP.BODY_SHOP_MANAGER,
								},
							},
						],
					},
					{
						id: 1,
						menuItems: [
							{
								label: "Body Shop Prod. Manager",
								description: "Maintains industry quality standards",
								icon: getPositionIcon({ position: positions.BODY_SHOP.BODY_SHOP_PRODUCT_MANAGER }),
								url: "/washouts/body-shop-product-manager",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_BODY_SHOP_PRODUCT_MANAGER_WASHOUTS,
									position: positions.BODY_SHOP.BODY_SHOP_PRODUCT_MANAGER,
								},
							},
						],
					},
					{
						id: 2,
						menuItems: [
							{
								label: "Body Shop Foreman",
								description: "Foresees the body shop department",
								icon: getPositionIcon({ position: positions.BODY_SHOP.BODY_SHOP_FOREMAN }),
								url: "/washouts/body-shop-foreman",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_BODY_SHOP_FOREMAN_WASHOUTS,
									position: positions.BODY_SHOP.BODY_SHOP_FOREMAN,
								},
							},
						],
					},
					{
						id: 3,
						menuItems: [
							{
								label: "Body Shop Estimator",
								description: "Estimates standard and custom paint jobs",
								icon: getPositionIcon({ position: positions.BODY_SHOP.BODY_SHOP_ESTIMATOR }),
								url: "/washouts/body-shop-estimator",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_BODY_SHOP_ESTIMATOR_WASHOUTS,
									position: positions.BODY_SHOP.BODY_SHOP_ESTIMATOR,
								},
							},
						],
					},
					{
						id: 4,
						menuItems: [
							{
								label: "Body Shop Painter",
								description: "Arranges standard and custom paint jobs",
								icon: getPositionIcon({ position: positions.BODY_SHOP.BODY_SHOP_PAINTER }),
								url: "/washouts/body-shop-painter",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_BODY_SHOP_PAINTER_WASHOUTS,
									position: positions.BODY_SHOP.BODY_SHOP_PAINTER,
								},
							},
						],
					},
					{
						id: 5,
						menuItems: [
							{
								label: "Body Shop Technician",
								description: "Repairs body parts and bodies of vehicles",
								icon: getPositionIcon({ position: positions.BODY_SHOP.BODY_SHOP_TECHNICIAN }),
								url: "/washouts/body-shop-technician",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_BODY_SHOP_TECHNICIAN_WASHOUTS,
									position: positions.BODY_SHOP.BODY_SHOP_TECHNICIAN,
								},
							},
						],
					},
				],
			},
			{
				name: "Parts",
				menuItemGroups: [
					{
						id: 0,
						menuItems: [
							{
								label: "Parts Directors",
								description: "Chief of all things parts",
								icon: getPositionIcon({ position: positions.PARTS.PARTS_DIRECTOR }),
								url: "/washouts/parts-director",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_PARTS_DIRECTOR_WASHOUTS,
									position: positions.PARTS.PARTS_DIRECTOR,
								},
							},
						],
					},
					{
						id: 1,
						menuItems: [
							{
								label: "Parts Manager",
								description: "Oversees the parts department",
								icon: getPositionIcon({ position: positions.PARTS.PARTS_MANAGER }),
								url: "/washouts/parts-manager",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_PARTS_MANAGER_WASHOUTS,
									position: positions.PARTS.PARTS_MANAGER,
								},
							},
						],
					},
					{
						id: 2,
						menuItems: [
							{
								label: "Parts Counter Associate",
								description: "Parts sales & customer service",
								icon: getPositionIcon({ position: positions.PARTS.PARTS_COUNTER_ASSOCIATE }),
								url: "/washouts/parts-counter-associate",
								accessConfiguration: {
									type: "granted",
									access: actions.VIEW_PARTS_COUNTER_ASSOCIATE_WASHOUTS,
									position: positions.PARTS.PARTS_COUNTER_ASSOCIATE,
								},
							},
						],
					},
				],
			},
		],
		footerItems: [
			{
				label: "Washout Review",
				icon: WashoutReview,
				iconHover: WashoutReviewDark,
				url: "/washout-review",
				accessConfiguration: {
					type: "authorized",
					access: actions.VIEW_WASHOUT_REVIEW,
					extraAuthorization: ["enableReviewWashout"],
				},
			},
			{
				label: "Payroll Closing",
				icon: PayrollClosing,
				iconHover: PayrollClosingDark,
				url: "/payroll-closing",
				accessConfiguration: {
					type: "authorized",
					access: actions.VIEW_COMMISSION_CYCLE_CLOSING,
				},
			},
			{
				label: "Support Center",
				icon: SupportCenter,
				iconHover: SupportCenterDark,
				url: "/support-center",
				accessConfiguration: {
					type: "authorized",
					access: actions.VIEW_SUPPORT_CENTER,
				},
			},
		],
	},
};

export default WashoutsMenuConfiguration;
